/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  Organisation,
  OrganisationApiResponse,
  PaginationData,
  TransformedOrganisationsResponse,
  NestedOrganisationsResponse,
  DoubleNestedResponse,
  TransformedOrganisationForTable
} from '#types/organisation';

const isNestedOrganisationsResponse = (
  data: unknown
): data is NestedOrganisationsResponse['data'] => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'organisations' in data &&
    Array.isArray((data as any).organisations)
  );
};

const isDoubleNestedResponse = (
  data: unknown
): data is DoubleNestedResponse['data'] => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'data' in data &&
    Array.isArray((data as any).data)
  );
};

const extractOrganisationsArray = (
  response: OrganisationApiResponse
): Organisation[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (isNestedOrganisationsResponse(response?.data))
    return response.data.organisations;
  if (isDoubleNestedResponse(response?.data)) return response.data.data;
  return [];
};

const extractPagination = (
  response: OrganisationApiResponse
): PaginationData => {
  if (Array.isArray(response)) return {};
  if (!response || typeof response !== 'object') return {};

  if ('pagination' in response && response.pagination) {
    return response.pagination;
  }

  if (
    'data' in response &&
    response.data &&
    typeof response.data === 'object' &&
    'pagination' in response.data
  ) {
    return (response.data as any).pagination || {};
  }

  return {};
};

export const getOrganisationsResponseTransformer = (
  response: OrganisationApiResponse
): TransformedOrganisationsResponse => {
  const organisations = extractOrganisationsArray(response);
  const pagination = extractPagination(response);

  return {
    organisations,
    pagination: {
      total: pagination?.total ?? organisations.length,
      totalPages: pagination?.total_pages ?? pagination?.totalPages ?? 1,
      currentPage:
        pagination?.current_page ??
        pagination?.currentPage ??
        pagination?.page ??
        1,
      limit: pagination?.limit ?? 10,
      hasMore: pagination?.has_more ?? pagination?.hasMore ?? false
    }
  };
};

// Transform individual organisation data to match table expectations
export const transformOrganisationForTable = (
  org: Organisation
): TransformedOrganisationForTable => {
  return {
    id: org.id,
    name: org.name,
    photo: org.logo || org.photo || null, // Map logo to photo for table compatibility
    totalOffers: (org as any).total_offers || (org as any).totalOffers || '0',
    subscribers: (org as any).subscribers || '0',
    benefitsProvided:
      (org as any).benefits_provided || (org as any).benefitsProvided || 0
  };
};

const extractRequestsArray = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const extractRequestsPagination = (response: any): any => {
  if (Array.isArray(response)) return {};
  return response?.pagination || response?.data?.pagination || {};
};

// Transform organisation requests data
export const getOrganisationRequestsResponseTransformer = (response: any) => {
  const requests = extractRequestsArray(response);
  const pagination = extractRequestsPagination(response);

  return {
    requests,
    pagination: {
      total: pagination?.total ?? requests.length,
      totalPages: pagination?.total_pages ?? pagination?.totalPages ?? 1,
      currentPage:
        pagination?.current_page ??
        pagination?.currentPage ??
        pagination?.page ??
        1,
      limit: pagination?.limit ?? 10,
      hasMore: pagination?.has_more ?? pagination?.hasMore ?? false
    }
  };
};
